import React, { useEffect, useState } from 'react';
import { financeiroService } from '../../services/financeiroService';
import { storageService } from '../../services/storageService';
import { pessoaService } from '../../services/pessoaService';
import { empresaService } from '../../services/empresaService';
import { Conta, Categoria, FormaPagamento, GatewayPagamento, Banco, TipoContaId, TipoAnexoId } from '../../types/financeiro';
import { Pessoa } from '../../types/pessoa';
import { EmpresaConfig } from '../../types/empresa';
import { PessoaSearchSelect } from '../common/PessoaSearchSelect';
import { parseContaObs, buildContaObs } from '../../utils/financeiroMetadataUtils';
import { sanitizeText } from '../../utils/textSanitizer';
import { usePermissions } from '../../hooks/usePermissions';
import { MaskedValue } from '../common/ProtectedField';
import { ContaAnexosManager, PendingAnexo } from './ContaAnexosManager';
import { 
  X, 
  Save, 
  Eraser, 
  ArrowLeft, 
  Receipt, 
  Calendar, 
  DollarSign, 
  Upload, 
  FileText, 
  Trash2, 
  CheckCircle2,
  Building2,
  CreditCard,
  Layers,
  Lock,
  Landmark,
  Paperclip
} from 'lucide-react';

interface ContaFinanceiraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedConta?: Conta | null;
  mode?: 'edit' | 'quitar' | 'create' | 'view';
  tipoConta: TipoContaId;
}

export function ContaFinanceiraModal({ 
  isOpen, 
  onClose, 
  onSave, 
  selectedConta, 
  mode = 'create',
  tipoConta 
}: ContaFinanceiraModalProps) {
  const { hasPermission } = usePermissions();
  const canViewValues = hasPermission('view_financial_values');
  const canManage = hasPermission('manage_financial');
  const canCreate = hasPermission('create_financial');
  const [loading, setLoading] = useState(false);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [gateways, setGateways] = useState<GatewayPagamento[]>([]);
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaConfig[]>([]);

  // Form fields
  const [pessoaId, setPessoaId] = useState<number>(0);
  const [categoriaId, setCategoriaId] = useState<number>(0);
  const [formaPagamentoId, setFormaPagamentoId] = useState<number>(0);
  const [gatewayPagamentoId, setGatewayPagamentoId] = useState<number | undefined>(undefined);
  const [bancoId, setBancoId] = useState<number>(0);
  const [bancoNome, setBancoNome] = useState<string>('');
  const [cnpjFaturador, setCnpjFaturador] = useState<string>('');
  const [descricao, setDescricao] = useState('');
  const [numeroNf, setNumeroNf] = useState('');
  const [dtEmissao, setDtEmissao] = useState(new Date().toISOString().split('T')[0]);
  const [dtVencimento, setDtVencimento] = useState('');
  const [dtPagamento, setDtPagamento] = useState('');
  const [valor, setValor] = useState<number>(0);
  const [valorPago, setValorPago] = useState<number | undefined>(undefined);
  const [parcela, setParcela] = useState<number>(1);
  const [observacao, setObservacao] = useState('');
  const [comprovanteUrl, setComprovanteUrl] = useState<string>('');
  const [comprovanteNome, setComprovanteNome] = useState<string>('');
  const [anexos, setAnexos] = useState<PendingAnexo[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadDependencies();
      if (selectedConta) {
        const meta = parseContaObs(selectedConta.obs, selectedConta.descricao);
        setPessoaId(selectedConta.pessoa_id || 0);
        setCategoriaId(selectedConta.categoria_id || 0);
        setFormaPagamentoId(selectedConta.forma_pagamento_id || 0);
        setGatewayPagamentoId(selectedConta.gateway_pagamento_id || undefined);
        setBancoId(selectedConta.banco_id || meta.banco_id || 0);
        setBancoNome(selectedConta.banco_nome || meta.banco_nome || '');
        setCnpjFaturador(selectedConta.cnpj_faturador || meta.cnpj_faturador || '');
        setDescricao(selectedConta.descricao || '');
        setNumeroNf(selectedConta.numero_nf || meta.numero_nf || '');
        setDtEmissao(selectedConta.dt_emissao || new Date().toISOString().split('T')[0]);
        setDtVencimento(selectedConta.dt_vencimento || '');
        setDtPagamento(mode === 'quitar' ? new Date().toISOString().split('T')[0] : (selectedConta.dt_pagamento || ''));
        setValor(selectedConta.valor || 0);
        setValorPago(selectedConta.valor_pago || meta.valor_pago || (selectedConta.dt_pagamento ? selectedConta.valor : undefined));
        setParcela(selectedConta.parcela || 1);
        setObservacao(meta.observacao_pura || selectedConta.obs || '');
        setComprovanteUrl(selectedConta.comprovante_url || meta.comprovante_url || '');
        setComprovanteNome(meta.comprovante_nome || (meta.comprovante_url ? 'Comprovante' : ''));

        // Load attachments
        loadAnexos(selectedConta);
      } else {
        handleClear();
      }
    }
  }, [isOpen, selectedConta, mode, tipoConta]);

  async function loadAnexos(conta: Conta) {
    try {
      const dbAnexos = await financeiroService.getAnexosConta(conta.id);
      const pending: PendingAnexo[] = dbAnexos.map(a => ({
        id: a.id,
        tipo_anexo_id: a.tipo_anexo_id,
        tipo_nome: a.tipo_anexo?.nome || (
          a.tipo_anexo_id === TipoAnexoId.BOLETO ? 'Boleto' :
          a.tipo_anexo_id === TipoAnexoId.NOTA_FISCAL ? 'Nota Fiscal' :
          a.tipo_anexo_id === TipoAnexoId.COMPROVANTE ? 'Comprovante de Pagamento' :
          a.tipo_anexo_id === TipoAnexoId.RECIBO ? 'Recibo' :
          a.tipo_anexo_id === TipoAnexoId.FATURA ? 'Fatura' : 'Outros'
        ),
        descricao: a.descricao,
        arquivo: a.arquivo,
        nome_arquivo: a.descricao,
        isExisting: true
      }));
      setAnexos(pending);
    } catch (err) {
      console.warn('Erro ao carregar anexos da conta:', err);
    }
  }

  async function loadDependencies() {
    try {
      const [p, c, f, g, b, emps] = await Promise.all([
        pessoaService.getAll(),
        financeiroService.getCategorias(),
        financeiroService.getFormasPagamento(),
        financeiroService.getGateways(),
        financeiroService.getBancos(),
        empresaService.getEmpresas()
      ]);
      setPessoas(p);
      setCategorias(c.filter(cat => Number(cat.tipo_conta_id) === Number(tipoConta)));
      setFormasPagamento(f);
      setGateways(g);
      setBancos(b.filter(item => !item.deleted_at));
      setEmpresas(emps);
    } catch (err) {
      console.error('Error loading dependencies:', err);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pessoaId || !formaPagamentoId) {
      alert(`Por favor, preencha os campos obrigatórios (${tipoConta === TipoContaId.RECEBER ? 'Cliente' : 'Favorecido/Fornecedor'} e Forma de Pagamento).`);
      return;
    }

    if (!valor || valor <= 0) {
      alert('O valor da conta deve ser maior que zero.');
      return;
    }

    setLoading(true);
    try {
      const selectedBanco = bancos.find(b => Number(b.id) === Number(bancoId));

      // Separate primary documents from the anexos list
      const boletoItem = anexos.find(a => a.tipo_anexo_id === TipoAnexoId.BOLETO);
      const nfItem = anexos.find(a => a.tipo_anexo_id === TipoAnexoId.NOTA_FISCAL);
      const comprovanteItem = anexos.find(a => a.tipo_anexo_id === TipoAnexoId.COMPROVANTE);

      // Only insert new attachments (isExisting is false or not defined)
      const novosAnexosToSave = anexos
        .filter(a => !a.isExisting && a.arquivo)
        .map(a => ({
          tipo_anexo_id: a.tipo_anexo_id,
          descricao: a.descricao || a.tipo_nome,
          arquivo: a.arquivo
        }));

      const payload: any = {
        tipo_conta_id: tipoConta,
        pessoa_id: pessoaId,
        categoria_id: categoriaId || null,
        forma_pagamento_id: formaPagamentoId,
        gateway_pagamento_id: gatewayPagamentoId || null,
        banco_id: bancoId || null,
        banco_nome: selectedBanco?.nome || bancoNome || null,
        cnpj_faturador: cnpjFaturador.trim() || null,
        descricao: descricao.trim() || `Lançamento ${tipoConta === TipoContaId.RECEBER ? 'Receita' : 'Despesa'}`,
        dt_emissao: dtEmissao || null,
        dt_vencimento: dtVencimento || null,
        dt_pagamento: dtPagamento || null,
        valor: valor,
        valor_pago: dtPagamento ? (valorPago || valor) : null,
        parcela: parcela || 1,
        numero_nf: numeroNf.trim() || null,
        comprovante_url: comprovanteItem ? comprovanteItem.arquivo : null,
        comprovante_nome: comprovanteItem ? (comprovanteItem.descricao || comprovanteItem.nome_arquivo || 'Comprovante') : null,
        boleto_url: boletoItem ? boletoItem.arquivo : null,
        boleto_nome: boletoItem ? (boletoItem.descricao || boletoItem.nome_arquivo || 'Boleto') : null,
        nf_url: nfItem ? nfItem.arquivo : null,
        nf_nome: nfItem ? (nfItem.descricao || nfItem.nome_arquivo || 'Nota Fiscal') : null,
        obs: observacao.trim() || null,
        anexos: anexos,
        novos_anexos: novosAnexosToSave,
      };

      if (selectedConta?.id && mode !== 'create') {
        await financeiroService.updateConta(selectedConta.id, payload);
      } else {
        await financeiroService.createConta(payload as Omit<Conta, 'id'>);
      }

      onSave();
      onClose();
    } catch (err: any) {
      alert('Erro ao salvar conta: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPessoaId(0);
    setCategoriaId(0);
    setFormaPagamentoId(0);
    setGatewayPagamentoId(undefined);
    setBancoId(0);
    setBancoNome('');
    setCnpjFaturador('');
    setDescricao('');
    setNumeroNf('');
    setDtEmissao(new Date().toISOString().split('T')[0]);
    setDtVencimento('');
    setDtPagamento('');
    setValor(0);
    setValorPago(undefined);
    setParcela(1);
    setObservacao('');
    setComprovanteUrl('');
    setComprovanteNome('');
    setAnexos([]);
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'quitar' || mode === 'view';
  const isViewOnly = mode === 'view';
  const isReceita = Number(tipoConta) === TipoContaId.RECEBER;
  const labelPessoa = isReceita ? 'Cliente (Tomador do Serviço)' : 'Fornecedor / Agregado / Motorista (Favorecido)';
  const labelTitulo = isReceita ? 'Conta a Receber' : 'Conta a Pagar';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col border border-slate-200 max-h-[92vh] animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b flex-shrink-0 ${
          isReceita ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'
        }`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-xl">
              {isReceita ? <DollarSign className="w-6 h-6 text-white" /> : <Receipt className="w-6 h-6 text-white" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {mode === 'view' ? `Visualizar ${labelTitulo}` : mode === 'edit' ? `Editar ${labelTitulo}` : mode === 'quitar' ? `Quitar / Baixar ${labelTitulo}` : `Nova ${labelTitulo}`}
              </h2>
              <p className="text-xs text-white/80">
                {selectedConta?.id ? `Lançamento Financeiro #${selectedConta.id}` : 'Preencha os dados da conta financeira'}
              </p>
            </div>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          
          {/* Seção 1: Identificação & Favorecido */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-slate-500" />
              1. Identificação do Favorecido / Cliente
            </h3>
            
            <PessoaSearchSelect
              label={labelPessoa}
              required
              disabled={isReadOnly}
              value={pessoaId}
              onChange={(pId) => setPessoaId(pId)}
              pessoas={pessoas}
              tipoConta={tipoConta}
              showGroupSelect={true}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Descrição do Lançamento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  disabled={isViewOnly}
                  placeholder="Ex: Faturamento Mensal, Abastecimento, Peças, Repasse..."
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 focus:border-red-500 focus:ring-red-500 bg-white"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Categoria Financeira
                </label>
                <select
                  disabled={isReadOnly}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
                  value={categoriaId}
                  onChange={(e) => setCategoriaId(Number(e.target.value))}
                >
                  <option value={0}>Selecione uma categoria...</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {sanitizeText(c.nome)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Seção 2: Dados Fiscais, Bancários & Pagamento */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
              <Receipt className="w-4 h-4 text-slate-500" />
              2. Dados Fiscais, Bancários & Pagamento
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Banco / Conta Financeira */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Landmark className="w-3.5 h-3.5 text-blue-600" />
                  Banco / Conta Bancária
                </label>
                <select
                  disabled={isViewOnly}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-blue-500 focus:ring-blue-500 bg-white"
                  value={bancoId || 0}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    setBancoId(id);
                    const b = bancos.find(x => Number(x.id) === id);
                    if (b?.nome) setBancoNome(b.nome);
                  }}
                >
                  <option value={0}>Selecione o banco (Ex: Banco do Brasil, BTG Pactual)...</option>
                  {bancos.map((b) => (
                    <option key={b.id} value={b.id}>
                      {sanitizeText(b.nome)}
                    </option>
                  ))}
                </select>
              </div>

              {/* CNPJ Faturador / Empresa */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                  Faturado pelo CNPJ / Empresa
                </label>
                <input
                  type="text"
                  disabled={isViewOnly}
                  placeholder="Ex: CNPJ 1 (Matriz) ou CNPJ 2"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
                  value={cnpjFaturador}
                  onChange={(e) => setCnpjFaturador(e.target.value)}
                />
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {empresas.map((emp, idx) => {
                    const label = emp.cnpj && emp.cnpj.length > 5 
                      ? `${emp.cnpj} - ${emp.nome_fantasia || emp.razao_social}` 
                      : emp.nome_fantasia || emp.razao_social || `Empresa ${idx + 1}`;
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        onClick={() => setCnpjFaturador(label)}
                        className="text-[10px] bg-slate-200/80 hover:bg-slate-300 text-slate-700 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                      >
                        {emp.nome_fantasia || emp.razao_social}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Nº Nota Fiscal (NF) / Doc
                </label>
                <input
                  type="text"
                  disabled={isViewOnly}
                  placeholder="Ex: NF-e 14205"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold text-slate-900 focus:border-red-500 focus:ring-red-500 bg-white"
                  value={numeroNf}
                  onChange={(e) => setNumeroNf(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Forma de Pagamento <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  disabled={isViewOnly}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
                  value={formaPagamentoId}
                  onChange={(e) => setFormaPagamentoId(Number(e.target.value))}
                >
                  <option value={0}>Selecione a forma...</option>
                  {formasPagamento.map((f) => (
                    <option key={f.id} value={f.id}>
                      {sanitizeText(f.nome)}
                    </option>
                  ))}
                </select>
              </div>

              {isReceita ? (
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Gateway de Pagamento
                  </label>
                  <select
                    disabled={isViewOnly}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
                    value={gatewayPagamentoId || 0}
                    onChange={(e) => setGatewayPagamentoId(Number(e.target.value) || undefined)}
                  >
                    <option value={0}>Nenhum / Direto</option>
                    {gateways.map((g) => (
                      <option key={g.id} value={g.id}>
                        {sanitizeText(g.nome)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Parcela / Número
                  </label>
                  <input
                    type="number"
                    min="1"
                    disabled={isReadOnly}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
                    value={parcela}
                    onChange={(e) => setParcela(Number(e.target.value) || 1)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Seção 3: Valores & Datas */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-slate-500" />
              3. Valores e Prazos
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Data de Emissão
                </label>
                <input
                  type="date"
                  disabled={isReadOnly}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
                  value={dtEmissao}
                  onChange={(e) => setDtEmissao(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Data de Vencimento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  disabled={isReadOnly}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold text-slate-900 focus:border-red-500 focus:ring-red-500 bg-white"
                  value={dtVencimento}
                  onChange={(e) => setDtVencimento(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Valor Nominal (R$) <span className="text-red-500">*</span>
                </label>
                {canViewValues ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    disabled={isReadOnly}
                    placeholder="0.00"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-extrabold text-slate-900 focus:border-red-500 focus:ring-red-500 bg-white"
                    value={valor || ''}
                    onChange={(e) => setValor(parseFloat(e.target.value) || 0)}
                  />
                ) : (
                  <div className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm flex items-center justify-between text-slate-400 font-mono select-none">
                    <span className="flex items-center gap-1.5">
                      <Lock className="w-4 h-4 text-slate-400" />
                      <span>••••••</span>
                    </span>
                    <span className="text-[10px] text-slate-500 font-sans uppercase font-bold">Confidencial</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-800 uppercase tracking-wider mb-1">
                  Data Pagamento / Baixa
                </label>
                <input
                  type="date"
                  disabled={isViewOnly}
                  className="w-full rounded-xl border border-emerald-300 bg-emerald-50/50 px-3 py-2 text-sm font-medium text-emerald-950 focus:border-emerald-500 focus:ring-emerald-500"
                  value={dtPagamento}
                  onChange={(e) => setDtPagamento(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Seção 4: Documentos e Anexos (Múltiplos: Boletos, Notas Fiscais, Comprovantes) */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Paperclip className="w-4 h-4 text-slate-500" />
                4. Anexos da Conta (Boleto, Nota Fiscal, Comprovante de Pagamento)
              </span>
              {anexos.length > 0 && (
                <span className="text-emerald-700 text-xs font-bold flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {anexos.length} {anexos.length === 1 ? 'Anexo' : 'Anexos'}
                </span>
              )}
            </h3>

            <ContaAnexosManager
              anexos={anexos}
              onChange={setAnexos}
              isReadOnly={isViewOnly}
            />
          </div>

          {/* Seção 5: Observações Gerais */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Observações Gerais & Detalhes
            </label>
            <textarea
              rows={2}
              disabled={isViewOnly}
              placeholder="Informações adicionais, detalhes bancários, observações de auditoria..."
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </div>

          {/* Footer Actions */}
          <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white pb-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" />
              {isViewOnly ? 'Fechar' : 'Voltar'}
            </button>
            {!isViewOnly && mode !== 'quitar' && (
              <button
                type="button"
                onClick={handleClear}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-colors cursor-pointer"
              >
                <Eraser className="h-4 w-4" />
                Limpar
              </button>
            )}
            {!isViewOnly && (
              <button
                type="submit"
                disabled={loading}
                className={`inline-flex items-center gap-2 px-6 py-2 text-xs font-bold text-white rounded-xl shadow-sm transition-colors cursor-pointer disabled:opacity-50 ${
                  isReceita ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                <Save className="h-4 w-4" />
                {loading ? 'Salvando...' : mode === 'quitar' ? 'Confirmar Baixa' : 'Salvar Lançamento'}
              </button>
            )}
          </div>
        </form>

      </div>
    </div>
  );
}
